'use client';

import React, { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef, memo } from 'react';
import { useNavigationType, useLocation } from 'react-router-dom';
import { cn } from '../../utils/tailwindUtils';
import { Button } from './button';
import { Input } from './input';
import { SelectField } from './form-fields';
import useApiMethods from '../../lib/useApiMethods';
import { getCSVData, getPrintData } from '../common/CommonFunctions';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import ToastMsg from '../common/ToastMsg';
import { BRANCH_FILTER_CHANGED_EVENT, getListBranchQuery, getListBranchScopeKey, isOrgAdminUser } from '../../utils/branchSession';
import { Ban, Check, FileSearch, Hourglass, PieChart, Send, X } from 'lucide-react';

// --- Back-navigation state cache ---
// Stores per-route table state (filters, sort, pagination, scroll) in
// sessionStorage so returning via browser Back / navigate(-1) restores the
// previous view. Cleared on any non-POP navigation, so reaching the list via
// the side menu (PUSH) yields a fresh state.
const STATE_CACHE_PREFIX = 'reactable.state:';
const serializeDayjs = (v) => (dayjs.isDayjs(v) ? v.toISOString() : (v ?? null));
const restoreDayjs = (v) => (v ? dayjs(v) : null);

// --- Reusable SVG Icons ---

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-gray-400"><path d="m21 21-4.3-4.3" /><circle cx="11" cy="11" r="8" /></svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
);

const ExcelIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" /></svg>
);

const PDFIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" /><rect x="6" y="14" width="12" height="8" rx="1" /></svg>
);

const DraftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" /></svg>
);

const SortIcon = ({ direction }) => {
    if (direction === 'none') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2 opacity-30"><path d="m7 15-3 3 3 3" /><path d="m20 9-3-3-3 3" /><path d="M4 18v-6a4 4 0 0 1 4-4h12" /><path d="M17 6v6a4 4 0 0 1-4 4H4" /></svg>;
    }
    if (direction === 'asc') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" /></svg>;
    }
    return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 8 4-4 4 4" /><path d="M7 4v16" /><path d="m21 16-4 4-4-4" /><path d="M17 20V4" /></svg>;
};

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m15 18-6-6 6-6" /></svg>
);

const ChevronRightIcon = ({ className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-5 w-5 ${className}`}><path d="m9 18 6-6-6-6" /></svg>
);

const ChevronsLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);

const ChevronsRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m16 22-1-4" /><path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1" /><path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z" /><path d="m8 22 1-4" /></svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

const XIcon = ({ className = "h-5 w-5" }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// --- Right Sidebar Filter Component ---
export const FilterSidebar = ({
    isOpen,
    onClose,
    filterableColumns,
    tempFilters,
    setTempFilters,
    onApply,
    onClear
}) => {
    return (
        <div
            className={cn(
                "fixed top-0 right-0 h-full w-80 shadow-lg transform transition-transform duration-300 ease-in-out z-50 border-l",
                "bg-white border-gray-200 text-gray-800", // light theme
                "dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100", // dark theme
                isOpen ? "translate-x-0" : "translate-x-full"
            )}
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold">Filter Data</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                    <CloseIcon />
                </button>
            </div>

            {/* Content */}
            <div className="p-4 h-[calc(100%-120px)] overflow-y-auto">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Filter data by specific columns.
                </p>
                <div className="space-y-4">
                    {filterableColumns.map(({ accessor, Header, filterType, filterOptions }) => (
                        <div key={accessor} className="flex flex-col gap-2">
                            <label
                                htmlFor={accessor}
                                className="text-xs font-medium text-gray-700 dark:text-gray-300"
                            >
                                {Header}
                            </label>

                            {accessor === "is_active" ? (
                                // Custom filter for Status
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            setTempFilters((prev) => ({ ...prev, is_active: "True" }))
                                        }
                                        className={`px-3 py-1 rounded text-xs font-medium border transition-colors
            ${tempFilters["is_active"] === "True"
                                                ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300"
                                                : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                            }`}
                                    >
                                        Active
                                    </button>
                                    <button
                                        onClick={() =>
                                            setTempFilters((prev) => ({ ...prev, is_active: "False" }))
                                        }
                                        className={`px-3 py-1 rounded text-xs font-medium border transition-colors
            ${tempFilters["is_active"] === "False"
                                                ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300"
                                                : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                            }`}
                                    >
                                        Inactive
                                    </button>
                                </div>
                            ) : filterType === 'date' ? (
                                // Date Picker for date fields
                                <DatePicker
                                    value={tempFilters[accessor] ? dayjs(tempFilters[accessor]) : null}
                                    onChange={(dateObj, dateString) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: dateString || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD"
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={`Select ${Header}...`}
                                />
                            ) : filterType === 'month' ? (
                                <DatePicker
                                    value={tempFilters[accessor] ? dayjs().month(tempFilters[accessor] - 1) : null}
                                    onChange={(dateObj, dateString) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: dateObj ? String(dateObj.month() + 1).padStart(2, '0') : "" // "01", "02", etc.
                                        }));
                                    }}
                                    format="MMMM"
                                    picker="month"
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={`Select ${Header}...`}
                                />
                            ) : filterType === 'range' ? (
                                // Range Picker for date ranges
                                <DatePicker.RangePicker
                                    value={[
                                        tempFilters[`${accessor}_from`] ? dayjs(tempFilters[`${accessor}_from`]) : null,
                                        tempFilters[`${accessor}_to`] ? dayjs(tempFilters[`${accessor}_to`]) : null
                                    ]}
                                    onChange={(dates, dateStrings) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [`${accessor}_from`]: dateStrings[0] || "",
                                            [`${accessor}_to`]: dateStrings[1] || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD"
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={['Start date', 'End date']}
                                />
                            ) : filterType === 'datetime' ? (
                                // Date Time Picker for datetime fields
                                <DatePicker
                                    value={tempFilters[accessor] ? dayjs(tempFilters[accessor]) : null}
                                    onChange={(dateObj, dateString) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: dateString || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD HH:mm"
                                    showTime={{ format: 'HH:mm' }}
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={`Select ${Header}...`}
                                />
                            ) : filterType === 'requestStatus' ? (
                                // Custom filter for request status. Each status has its
                                // own selected palette + a ring so the active chip is
                                // visually distinct from the others (previously cancelled
                                // and processed shared a gray fallback that looked
                                // unselected).
                                (() => {
                                    const selectedPalette = {
                                        pending: "bg-yellow-500 text-white border-yellow-600 ring-2 ring-yellow-300 dark:ring-yellow-700",
                                        approved: "bg-sky-500 text-white border-sky-600 ring-2 ring-sky-300 dark:ring-sky-700",
                                        partial: "bg-amber-500 text-white border-amber-600 ring-2 ring-amber-300 dark:ring-amber-700",
                                        rejected: "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-700",
                                        cancelled: "bg-slate-600 text-white border-slate-700 ring-2 ring-slate-300 dark:ring-slate-700",
                                        processed: "bg-emerald-600 text-white border-emerald-700 ring-2 ring-emerald-300 dark:ring-emerald-700",
                                    };
                                    const unselected = "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700";
                                    return (
                                        <div className="flex gap-2 flex-wrap">
                                            {Object.keys(selectedPalette).map((status) => {
                                                const isSelected = tempFilters[accessor] === status;
                                                return (
                                                    <button
                                                        key={status}
                                                        onClick={() =>
                                                            setTempFilters((prev) => ({
                                                                ...prev,
                                                                // Click again to clear, so toggling off is one click.
                                                                [accessor]: isSelected ? "" : status,
                                                            }))
                                                        }
                                                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 capitalize shadow-sm ${
                                                            isSelected ? selectedPalette[status] : unselected
                                                        }`}
                                                    >
                                                        {status}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                })()
                            ) : filterType === 'select' ? (
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    value={tempFilters[accessor] || undefined}
                                    onChange={(value) =>
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: value || "",
                                        }))
                                    }
                                    placeholder={`Filter by ${Header}...`}
                                    className="w-full [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!items-center [&_.ant-select-selection-item]:!text-xs [&_.ant-select-selection-placeholder]:!text-xs"
                                    options={filterOptions || []}
                                />
                            ) : (
                                <Input
                                    id={accessor}
                                    value={tempFilters[accessor] || ""}
                                    onChange={(e) =>
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: e.target.value,
                                        }))
                                    }
                                    placeholder={`Filter by ${Header}...`}
                                    className="h-9 placeholder:text-xs"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <div className="flex justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClear}
                        className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                        Clear All
                    </Button>
                    <Button
                        size="sm"
                        variant="brand"
                        onClick={onApply}
                    >
                        Apply Filters
                    </Button>
                </div>
            </div>
        </div>
    );
};

// --- Status filter config ---
// One source of truth for the approval-inbox status filter — used by both the
// desktop chip row and the mobile select. Each entry owns its display strings,
// icon component, and palette so the JSX below stays small.
const STATUS_CONFIG = [
    {
        key: 'pending', label: 'Pending', shortLabel: 'Pend', emoji: '⏳',
        Icon: Hourglass, iconClass: 'text-yellow-600',
        activeClass: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        idleClass: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100',
    },
    {
        key: 'approved', label: 'Approved', shortLabel: 'Appr', emoji: '✅',
        Icon: Send, iconClass: 'text-green-600',
        activeClass: 'bg-green-100 text-green-700 border-green-300',
        idleClass: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100',
    },
    {
        key: 'partial', label: 'Partial', shortLabel: 'Part', emoji: '◐',
        Icon: PieChart, iconClass: 'text-amber-600',
        activeClass: 'bg-amber-100 text-amber-700 border-amber-300',
        idleClass: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100',
    },
    {
        key: 'rejected', label: 'Rejected', shortLabel: 'Rej', emoji: '❌',
        Icon: X, iconClass: 'text-red-600',
        activeClass: 'bg-red-100 text-red-700 border-red-300',
        idleClass: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100',
    },
    {
        key: 'cancelled', label: 'Cancelled', shortLabel: 'Cancel', emoji: '🚫',
        Icon: Ban, iconClass: 'text-gray-600',
        activeClass: 'bg-gray-100 text-gray-600 border-gray-300',
        idleClass: 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100',
    },
    {
        key: 'processed', label: 'Processed', shortLabel: 'Proc', emoji: '✅',
        Icon: Check, iconClass: 'text-green-600',
        activeClass: 'bg-green-100 text-green-700 border-green-300',
        idleClass: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100',
    },
];

// Memoised so it only re-renders when its own props change — not on every
// Reactable state tick (loading, pagination, search input, etc.).
const StatusFilterChips = memo(function StatusFilterChips({
    activeStatus,
    extras = {},
    onStatusClick,
    onClearStatus,
}) {
    return (
        <>
            {/* Mobile: compact select */}
            <div className="md:hidden">
                <Tooltip>
                    <TooltipTrigger>
                        <Select
                            value={activeStatus || undefined}
                            onChange={(value) => (value ? onStatusClick(value) : onClearStatus())}
                            placeholder={<FileSearch className="w-4 h-4 text-gray-500" />}
                            size="large"
                            className={cn("custom-status-select", activeStatus ? 'w-16' : 'w-12')}
                            allowClear
                            onClear={onClearStatus}
                            suffixIcon={null}
                        >
                            {STATUS_CONFIG.map(({ key, Icon, iconClass }) => (
                                <Select.Option key={key} value={key} className={`status-option-${key}`}>
                                    <div className="flex items-center justify-center w-6 h-6">
                                        <Icon className={`w-4 h-4 ${iconClass}`} />
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                        Filter by Status
                    </TooltipContent>
                </Tooltip>
            </div>

            {/* Desktop: chip row */}
            <div className='hidden md:flex gap-2 items-center border-l px-2 border-gray-200'>
                {activeStatus && (
                    <button
                        onClick={onClearStatus}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 whitespace-nowrap"
                    >
                        <span className="hidden sm:inline">Clear Filter</span>
                        <span className="sm:hidden">✕</span>
                    </button>
                )}
                {STATUS_CONFIG.map(({ key, label, shortLabel, emoji, activeClass, idleClass }) => {
                    const isActive = activeStatus === key;
                    return (
                        <button
                            key={key}
                            onClick={() => onStatusClick(key)}
                            className={cn(
                                'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap',
                                isActive ? `${activeClass} scale-105 sm:scale-110` : idleClass
                            )}
                        >
                            <span className="mr-1">{emoji}</span>
                            <span className="hidden sm:inline">{label}</span>
                            <span className="sm:hidden">{shortLabel}</span>
                            <span className={cn("ml-1", isActive ? "font-semibold" : "font-normal")}>
                                {extras[key] ? `(${extras[key]})` : '(0)'}
                            </span>
                        </button>
                    );
                })}
            </div>
        </>
    );
});

// Date picker row — extracted + memoised so date/month/year state changes
// don't drag the rest of the header into a re-render.
const DateFilterBar = memo(function DateFilterBar({
    isDateRequired,
    isMonthYearRequired,
    isOptionalDateWithMonthYear,
    isFromToDateRequired,
    date, setDate,
    month, setMonth,
    year, setYear,
    fromDate, setFromDate,
    toDate, setToDate,
}) {
    if (isDateRequired) {
        return (
            <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(_, dateString) => setDate(dateString)}
                format="YYYY-MM-DD"
                picker="date"
                className="w-32"
            />
        );
    }
    if (isMonthYearRequired) {
        return (
            <>
                <DatePicker picker="month" value={month} onChange={setMonth} format="MMM" className="w-20" />
                <DatePicker picker="year" value={year} onChange={setYear} format="YYYY" className="w-20" />
                {isOptionalDateWithMonthYear && (
                    <DatePicker
                        value={date ? dayjs(date) : null}
                        onChange={(_, dateString) => setDate(dateString || "")}
                        format="DD MMM YYYY"
                        picker="date"
                        className="w-36"
                        allowClear
                        placeholder="All dates"
                    />
                )}
            </>
        );
    }
    if (isFromToDateRequired) {
        return (
            <>
                <DatePicker picker="date" value={fromDate} onChange={setFromDate} format="YYYY-MM-DD" className="w-32" />
                <DatePicker picker="date" value={toDate} onChange={setToDate} format="YYYY-MM-DD" className="w-32" />
            </>
        );
    }
    return null;
});

// --- TableHeader Component ---
const TableHeaderImpl = ({
    pagename,
    tableTitle,
    globalFilter,
    setGlobalFilter,
    children,
    filterableColumns,
    columnFilters,
    onColumnFilterChange,
    onApplyFilters,
    onClearFilters,
    sorting,
    pagination,
    isDraft,
    setIsDraft,
    url,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isOptionalDateWithMonthYear = false,
    isFromToDateRequired = false,
    date,
    setDate,
    month,
    setMonth,
    year,
    setYear,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    printFilter,
    extras = {},
}) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(columnFilters);
    const [showMobileSearch, setShowMobileSearch] = useState(false);

    // Treat empty string / null / undefined as "no filter", but accept 0 and
    // false as legitimate filter values (Boolean would drop them).
    const isFilterActive = useMemo(
        () => Object.values(columnFilters).some(v => v !== "" && v !== null && v !== undefined),
        [columnFilters]
    );

    const handleApply = () => {
        onApplyFilters(tempFilters);
        setSidebarOpen(false);
    };

    const handleClear = () => {
        setTempFilters({});
        onClearFilters();
    };

    useEffect(() => {
        setTempFilters(columnFilters);
    }, [columnFilters]);

    const handleExport = (type) => {
        const filters = {
            ...printFilter,
            search: globalFilter || "",
            ...columnFilters,
            page_no: (pagination?.pageIndex ?? 0) + 1,
            page_size: pagination?.pageSize ?? 10,
            column: sorting?.accessor || "",
            sort: sorting?.direction !== "none" ? sorting.direction : "",
            date: date ? dayjs(date).format("YYYY-MM-DD") : "",
            month: month ? dayjs(month).format("M") : "",
            year: year ? dayjs(year).format("YYYY") : "",
        };

        if (isMonthYearRequired && !isOptionalDateWithMonthYear) {
            delete filters['date']
        }

        if (isMonthYearRequired) {
            delete filters['from_date']
            delete filters['to_date']
        }

        if (isFromToDateRequired) {
            delete filters['date']
            delete filters['month']
            delete filters['year']
        }

        if (!isDateRequired && !isMonthYearRequired && !isFromToDateRequired) {
            delete filters['date']
            delete filters['month']
            delete filters['year']
        }

        const isPayslipList =
            pagename === "payslips" ||
            (url && String(url).toLowerCase().includes("payslip"));
        if (isPayslipList) {
            delete filters['month'];
            delete filters['year'];
            filters['payroll_month'] = month ? dayjs(month).format("M") : "";
            filters['payroll_year'] = year ? dayjs(year).format("YYYY") : "";
        }

        switch (type) {
            case "pdf":
                getPrintData(url, filters);
                break;
            case "csv":
            case "xlsx":
                getCSVData(url, filters, type);
                break;
            default:
                console.warn(`Unsupported export type: ${type}`);
        }
    };

    const toggleDraft = () => {
        setIsDraft((prev) => !prev);
    };

    // useCallback so memoised <StatusFilterChips> doesn't re-render every
    // time TableHeader re-renders for unrelated reasons.
    const handleStatusClick = useCallback((status) => {
        onApplyFilters({ ...columnFilters, approval_status: status });
    }, [onApplyFilters, columnFilters]);

    const handleClearStatus = useCallback(() => {
        const newFilters = { ...columnFilters };
        delete newFilters.approval_status;
        onApplyFilters(newFilters);
    }, [onApplyFilters, columnFilters]);

    return (
        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg border-b border-gray-200 dark:border-gray-700 relative shrink-0">
            {/* Floating Mobile Search Bar */}
            {showMobileSearch && (
                <div className="absolute top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 shadow-lg md:hidden">
                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            value={globalFilter || ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            className="block w-full pl-10 pr-10 py-3 border rounded-md leading-5 
                            bg-white text-gray-900 placeholder-gray-500 
                            focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-sidebar focus:border-sidebar-primary 
                            sm:text-xs dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                            placeholder="Search table..."
                            autoFocus
                        />
                        <button
                            onClick={() => setShowMobileSearch(false)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <XIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            {children && <div className="mb-2">{children}</div>}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 w-full md:w-auto">
                    {/* Excel Button - Green theme */}
                    {
                        isCSVRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-green-50 text-green-700 border border-green-200 
                                hover:bg-green-100 hover:border-green-300 hover:text-green-800
                                transition-colors duration-200
                                dark:bg-green-900/30 dark:text-green-300 dark:border-green-700
                                dark:hover:bg-green-900/50 dark:hover:text-green-200 dark:hover:border-green-600"
                                        onClick={() => handleExport('csv')}
                                    >
                                        <ExcelIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to CSV
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* PDF Button - Red theme */}
                    {
                        isPDFRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-red-50 text-red-700 border border-red-200 
                                hover:bg-red-100 hover:border-red-300 hover:text-red-800
                                transition-colors duration-200
                                dark:bg-red-900/30 dark:text-red-300 dark:border-red-700
                                dark:hover:bg-red-900/50 dark:hover:text-red-200 dark:hover:border-red-600"
                                        onClick={() => handleExport("pdf")}
                                    >
                                        <PDFIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to PDF
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* Draft Button - Blue theme */}
                    {
                        isDraftRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className={cn("flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium",
                                            "bg-sidebar/10 text-sidebar-primary border border-sidebar-primary/30",
                                            "hover:bg-sidebar/20 hover:border-sidebar-primary/50",
                                            "transition-colors duration-200",
                                            "dark:bg-sidebar/20 dark:text-sidebar-primary dark:border-sidebar-primary/40",
                                            "dark:hover:bg-sidebar/30",
                                            isDraft ? "ring-2 ring-offset-2 ring-sidebar-primary" : "")}
                                        onClick={toggleDraft}
                                    >
                                        <DraftIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Draft
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {pagename === 'approval inbox' && (
                        <StatusFilterChips
                            activeStatus={columnFilters.approval_status}
                            extras={extras}
                            onStatusClick={handleStatusClick}
                            onClearStatus={handleClearStatus}
                        />
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 w-full md:w-auto">
                    {/* Date Pickers - Hidden on mobile */}
                    <div className="hidden md:flex md:flex-row items-center gap-2">
                        <DateFilterBar
                            isDateRequired={isDateRequired}
                            isMonthYearRequired={isMonthYearRequired}
                            isOptionalDateWithMonthYear={isOptionalDateWithMonthYear}
                            isFromToDateRequired={isFromToDateRequired}
                            date={date} setDate={setDate}
                            month={month} setMonth={setMonth}
                            year={year} setYear={setYear}
                            fromDate={fromDate} setFromDate={setFromDate}
                            toDate={toDate} setToDate={setToDate}
                        />
                    </div>

                    {/* Mobile Search Toggle Button - Only shown on mobile */}
                    <div className="md:hidden">
                        <Tooltip>
                            <TooltipTrigger>
                                <button
                                    onClick={() => setShowMobileSearch(!showMobileSearch)}
                                    className="flex items-center justify-center w-10 h-10 border rounded-md shadow-sm text-xs font-medium 
                                    text-gray-700 bg-white hover:bg-gray-50 border-gray-300
                                    dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600"
                                >
                                    <SearchIcon />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                {showMobileSearch ? 'Hide Search' : 'Show Search'}
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    {/* Search Input - Hidden on mobile by default, shown when toggled */}
                    <div className={`${showMobileSearch ? 'hidden' : 'hidden'} md:block md:relative md:w-64`}>
                        <div className="relative w-full">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <SearchIcon />
                            </div>
                            <input
                                type="text"
                                value={globalFilter || ''}
                                onChange={e => setGlobalFilter(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 border rounded-md leading-5 
                                bg-white text-gray-900 placeholder-gray-500 
                                focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-sidebar focus:border-sidebar-primary 
                                sm:text-xs dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                                placeholder="Search table..."
                            />
                        </div>
                    </div>

                    {filterableColumns.length > 0 && (
                        <>
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        onClick={() => setSidebarOpen(true)}
                                        className=" relative flex items-center justify-center w-10 h-10 border rounded-md shadow-sm 
                                        text-xs font-medium 
                                        text-gray-700 bg-white hover:bg-gray-50 border-gray-300
                                        dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600"
                                    >
                                        <FilterIcon />
                                        {isFilterActive && (
                                            <span className="absolute -top-1 -right-1 block h-2.5 w-2.5 rounded-full bg-indigo-600 ring-2 ring-white" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Filter
                                </TooltipContent>
                            </Tooltip>

                            <FilterSidebar
                                isOpen={sidebarOpen}
                                onClose={() => setSidebarOpen(false)}
                                filterableColumns={filterableColumns}
                                tempFilters={tempFilters}
                                setTempFilters={setTempFilters}
                                onApply={handleApply}
                                onClear={handleClear}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// Memoised so Reactable state ticks that don't change header props (e.g. the
// table body re-rendering after a fetch resolves) no longer drag the whole
// header through a re-render too.
export const TableHeader = memo(TableHeaderImpl);

// --- Row alignment helper (pure, no hooks → safe at module scope) ---
const alignmentClassFor = (align) => {
    switch (align) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        default: return 'text-left';
    }
};

// --- Memoised TableRow ---
// Pulled out of Reactable so a parent-state change that doesn't touch row
// inputs (e.g. globalFilter, pagination, loading) no longer re-renders every
// cell. Cells with custom `Cell` renderers still re-render whenever the row
// object itself changes; that's intentional.
const TableRow = memo(function TableRow({
    row,
    rowIndex,
    rowKey,
    columns,
    renderSubComponent,
    isExpanded,
    toggleRow,
    onRowClick,
    onRowDoubleClick,
    highlightClass,
    rowPaddingClass,
    firstNonFixedColIndex,
    expandedContentColSpan,
}) {
    return (
        <React.Fragment>
            <tr
                className={cn(
                    "group transition-colors duration-150",
                    highlightClass || "hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
                onClick={() => onRowClick?.(row)}
                onDoubleClick={() => onRowDoubleClick?.(row)}
            >
                {renderSubComponent && (
                    <td className={cn(
                        "sticky left-0 z-20 px-3 sm:px-6 whitespace-nowrap text-xs text-gray-700 dark:text-gray-300",
                        highlightClass ? highlightClass.replace('border-l-4', '') : "bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800"
                    )}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleRow(rowIndex);
                            }}
                        >
                            <ChevronRightIcon
                                className={`transform transition-transform ${isExpanded ? "rotate-90" : ""}`}
                            />
                        </button>
                    </td>
                )}
                {columns.map(({ accessor, Cell, fixed, align }, colIndex) => (
                    <td
                        key={accessor}
                        className={cn(
                            "px-3 sm:px-6 whitespace-nowrap text-[13px] text-gray-700 dark:text-gray-300",
                            rowPaddingClass,
                            alignmentClassFor(align),
                            fixed && "sticky z-1 ",
                            highlightClass ? highlightClass.replace('border-l-4', '') : "group-hover:bg-gray-50 dark:group-hover:bg-gray-800",
                            fixed && (align === "right" ? "right-0" : "left-0"),
                            fixed && renderSubComponent && align !== "right" && "left-12",
                            highlightClass ? highlightClass.replace('border-l-4', '') :
                                fixed ? "bg-white dark:bg-gray-900" : ""
                        )}
                        style={
                            !fixed && colIndex === firstNonFixedColIndex && highlightClass && highlightClass.includes('border-l-4')
                                ? { borderLeft: '4px solid #10B981' }
                                : {}
                        }
                    >
                        {Cell ? <Cell value={row[accessor]} row={row} /> : row[accessor]}
                    </td>
                ))}
            </tr>
            {renderSubComponent && isExpanded && (
                <tr className={cn(
                    "bg-gray-50 dark:bg-gray-800",
                    highlightClass ? highlightClass.replace('border-l-4', '') : ""
                )}>
                    <td colSpan={expandedContentColSpan} className="p-0">
                        {renderSubComponent({ row })}
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
});

// --- DataTable Component (Renamed to Reactable) ---
export const Reactable = ({
    pagename = "",
    columns,
    data,
    selectedDate,
    selectedMonth,
    setData,
    setTotalCount = () => { },
    filter = {},
    setFetchFilter = () => { },
    tableTitle = "Data",
    renderSubComponent,
    children,
    tableHeight,
    fillHeight = true,
    className,
    rowSize = 'md',
    onRowClick,
    onRowDoubleClick,
    onRowHighlightKey = null,
    url,
    serverSide = false,
    serverTrigger = false,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isOptionalDateWithMonthYear = false,
    isFromToDateRequired = false,
    manualPagination = false,
    activeTab,
    printFilter,
    // When `true`, this table will not save/restore state across
    // back-navigation. Default is enabled.
    disableStateCache = false,
    // Optional explicit cache key. Defaults to `${prefix}${pathname}:${pagename}`
    // so a single page hosting multiple Reactables can disambiguate them.
    stateCacheKey,
    setWholeResponse = () => { },
}) => {
    // Read navigation type once per mount. POP = browser back/forward or
    // navigate(-1) — those should restore. PUSH/REPLACE = a fresh arrival
    // (side-menu link, navigate(path)), so we clear stale cache.
    const navigationType = useNavigationType();
    const location = useLocation();

    // Pinned cache key (frozen at mount via lazy useState).
    const [cacheKey] = useState(
        () => stateCacheKey ?? `${STATE_CACHE_PREFIX}${location.pathname}:${pagename}`
    );

    // One-shot restore — runs exactly once on first render.
    const [restored] = useState(() => {
        if (disableStateCache) return null;
        try {
            if (navigationType === 'POP') {
                const raw = sessionStorage.getItem(cacheKey);
                return raw ? JSON.parse(raw) : null;
            }
            // Fresh arrival — drop any stale snapshot so a later real Back
            // doesn't restore the wrong session's state.
            sessionStorage.removeItem(cacheKey);
            return null;
        } catch {
            return null;
        }
    });

    const [globalFilter, setGlobalFilter] = useState(restored?.globalFilter ?? '');
    const [columnFilters, setColumnFilters] = useState(restored?.columnFilters ?? {});
    const [sorting, setSorting] = useState(restored?.sorting ?? { accessor: '', direction: 'none' });
    const [pagination, setPagination] = useState(restored?.pagination ?? { pageIndex: 0, pageSize: 10 });
    const [jumpToPage, setJumpToPage] = useState('');
    const [expandedRows, setExpandedRows] = useState(restored?.expandedRows ?? {});
    const [totalRecords, setTotalRecords] = useState(0);
    const [isDraft, setIsDraft] = useState(restored?.isDraft ?? false);
    const [loading, setLoading] = useState(false);

    const [date, setDate] = useState(restored?.date ?? new Date().toISOString().split("T")[0]);

    // Date pickers hold dayjs objects in state, ISO strings in the cache.
    const [month, setMonth] = useState(
        restored?.month ? restoreDayjs(restored.month) : (selectedMonth ? selectedMonth : dayjs())
    );
    const [year, setYear] = useState(restored?.year ? restoreDayjs(restored.year) : dayjs());

    const [fromDate, setFromDate] = useState(
        restored?.fromDate ? restoreDayjs(restored.fromDate)
            : (selectedDate ? selectedDate?.from_date : dayjs())
    );
    const [toDate, setToDate] = useState(
        restored?.toDate ? restoreDayjs(restored.toDate)
            : (selectedDate ? selectedDate?.to_date : dayjs())
    );
    const [extras, setExtras] = useState({});

    // Scroll positions for window + (when tableHeight is set) the inner
    // scroll container. Refs avoid re-renders on scroll.
    const tableScrollContainerRef = useRef(null);
    const scrollSnapshotRef = useRef({
        window: restored?.scrollY ?? 0,
        table: restored?.tableScrollTop ?? 0,
    });
    const scrollRestoredRef = useRef(false);

    const { fetch } = useApiMethods();
    const filterableColumns = useMemo(() => columns.filter(c => c.filterable), [columns]);

    // Function to determine row highlight class
    const getRowHighlightClass = (row) => {

        if (!onRowHighlightKey) return '';

        let shouldHighlight = false;

        if (typeof onRowHighlightKey === 'function') {
            // Custom function provided
            shouldHighlight = onRowHighlightKey(row);
        } else {
            // String key provided - check if the value is truthy
            shouldHighlight = !!row[onRowHighlightKey];
        }

        return shouldHighlight
            ? 'bg-green-100 dark:bg-green-900'
            : '';
    };

    // Skip the very first run if we restored — otherwise the parent's
    // default `selectedMonth` would immediately clobber the restored value.
    const skipFirstSelectedMonthRef = useRef(restored != null);
    useEffect(() => {
        if (skipFirstSelectedMonthRef.current) {
            skipFirstSelectedMonthRef.current = false;
            return;
        }
        if (selectedMonth && isMonthYearRequired) {
            setMonth(selectedMonth);
            setYear(selectedMonth);
        }
    }, [selectedMonth, isMonthYearRequired]);

    // Stable hash of the parent-controlled `filter` object so the fetch effect
    // re-runs only when its contents change — not when the parent re-renders
    // and passes a fresh object identity. Avoids needing callers to memoise.
    const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);

    const [branchScopeKey, setBranchScopeKey] = useState(() => getListBranchScopeKey());

    useEffect(() => {
        const syncBranchScope = () => setBranchScopeKey(getListBranchScopeKey());
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, syncBranchScope);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, syncBranchScope);
    }, []);

    // Server-side data fetching.
    // Cancellation strategy: each effect run owns an AbortController; on
    // cleanup we abort the in-flight request AND set a `cancelled` flag
    // before we await, so stale responses can never overwrite fresh state.
    useEffect(() => {
        if (!serverSide) {
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Build filters object for API
                const filters = {
                    search: globalFilter || "",
                    ...filter,
                    ...columnFilters,
                    column: sorting.accessor || "",
                    sort: sorting.direction !== 'none' ? sorting.direction : "",
                    page_no: pagination.pageIndex + 1,
                    page_size: pagination.pageSize,
                    is_draft: isDraft ? "True" : "",
                    date: date ? dayjs(date).format("YYYY-MM-DD") : "",
                    month: month ? dayjs(month).format("M") : "",
                    year: year ? dayjs(year).format("YYYY") : "",
                    from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : "",
                    to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : "",
                };

                if (isOrgAdminUser()) {
                    Object.assign(filters, getListBranchQuery());
                }

                // Payslip API expects payroll_month / payroll_year (not month / year).
                const isPayslipList =
                    pagename === "payslips" ||
                    (url && String(url).toLowerCase().includes("payslip"));
                if (isPayslipList) {
                    delete filters['month'];
                    delete filters['year'];
                    filters['payroll_month'] = month ? dayjs(month).format("M") : "";
                    filters['payroll_year'] = year ? dayjs(year).format("YYYY") : "";
                }

                if (isMonthYearRequired && !isOptionalDateWithMonthYear) {
                    delete filters['date'];
                }

                if (isMonthYearRequired) {
                    delete filters['from_date'];
                    delete filters['to_date'];
                }

                if (isFromToDateRequired) {
                    delete filters['date'];
                    delete filters['month'];
                    delete filters['year'];
                }

                if (isDateRequired) {
                    delete filters['from_date'];
                    delete filters['to_date'];
                }

                if (!isDateRequired && !isMonthYearRequired && !isFromToDateRequired) {
                    delete filters['date'];
                    delete filters['month'];
                    delete filters['year'];
                    delete filters['from_date'];
                    delete filters['to_date'];
                }

                // Remove empty filters
                Object.keys(filters).forEach((key) => {
                    if (filters[key] === "" || filters[key] === null || filters[key] === undefined) {
                        delete filters[key];
                    }
                });

                const response = await fetch(url, filters, undefined, controller.signal);

                // Cancelled before/while awaiting — drop the response.
                if (cancelled || response?.status === 'cancelled') return;

                const listPayload = response?.data;
                if (listPayload && Array.isArray(listPayload.result)) {
                    setData(listPayload.result);
                    setExtras(listPayload.extras || {});
                    setTotalRecords(listPayload.total_count || 0);
                    setTotalCount(listPayload.total_request_count || listPayload.total_count || 0);
                    setFetchFilter(filters);
                    setWholeResponse(response);
                } else if (response?.status === 'success' && serverSide) {
                    // Empty/malformed list payload (e.g. branch filter with no rows) — clear stale rows.
                    setData([]);
                    setExtras(listPayload?.extras || {});
                    setTotalRecords(0);
                    setTotalCount(0);
                    setFetchFilter(filters);
                    setWholeResponse(response);
                }

                if (response?.status === 'fail' || (response?.detail && response?.status !== 'success')) {
                    const errMsg = response.message
                        || (Array.isArray(response.detail) ? response.detail.join(', ') : response.detail)
                        || 'Failed to fetch data.';
                    ToastMsg('error', errMsg);
                }
            } catch (err) {
                if (cancelled) return;
                console.error("Reactable API Error:", err);
                const errMsg = err?.message
                    || err?.response?.data?.message
                    || err?.response?.data?.detail
                    || 'Failed to fetch data.';
                ToastMsg('error', typeof errMsg === 'string' ? errMsg : 'Failed to fetch data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        // Debounce burst input (typing in search, rapid filter changes).
        const timeoutId = setTimeout(fetchData, 300);
        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timeoutId);
        };
    }, [
        url,
        serverSide,
        serverTrigger,
        globalFilter,
        columnFilters,
        sorting,
        pagination,
        isDraft,
        date,
        month,
        year,
        fromDate,
        toDate,
        filterKey,
        branchScopeKey,
    ]);

    // Clear table data only when activeTab actually changes — not on initial mount
    // (otherwise client-side tables that pass setData wipe parent state on first render).
    const prevActiveTabRef = useRef(activeTab);
    useEffect(() => {
        if (prevActiveTabRef.current !== activeTab) {
            setExtras({});
            setData([]);
            prevActiveTabRef.current = activeTab;
        }
    }, [activeTab, setData]);

    // Client-side filtering and sorting (for non-serverSide mode)
    const processedData = useMemo(() => {
        if (serverSide) {
            return data; // Use data directly from API when serverSide is true
        }

        let filtered = [...data];

        // Global filter
        if (globalFilter) {
            filtered = filtered.filter(row =>
                columns.some(({ accessor }) => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(globalFilter.toLowerCase());
                })
            );
        }

        // Column filters
        Object.entries(columnFilters).forEach(([accessor, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        // Sorting — locale-aware with numeric coercion so "10" sorts after
        // "2" and accented chars don't slip to the end. Nullish values go last.
        if (sorting.direction !== 'none') {
            const dir = sorting.direction === 'asc' ? 1 : -1;
            const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sorting.accessor];
                const bVal = b[sorting.accessor];
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return (aVal - bVal) * dir;
                }
                return collator.compare(String(aVal), String(bVal)) * dir;
            });
        }

        return filtered;
    }, [data, columns, globalFilter, columnFilters, sorting, serverSide]);

    // Calculate page count based on server-side or client-side
    const pageCount = serverSide
        ? Math.ceil(totalRecords / pagination.pageSize)
        : Math.ceil(processedData.length / pagination.pageSize);

    // Get data for current page
    const paginatedData = useMemo(() => {
        if (serverSide && !manualPagination) {
            return data; // API already returns paginated data
        }

        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return processedData?.slice(start, end);
    }, [processedData, pagination, serverSide, data, manualPagination]);

    const handleSort = (accessor) => {
        setSorting(prev => {
            if (prev.accessor === accessor) {
                if (prev.direction === 'asc') return { accessor, direction: 'desc' };
                if (prev.direction === 'desc') return { accessor, direction: 'none' };
            }
            return { accessor, direction: 'asc' };
        });
    };

    const handleJumpToPage = (e) => {
        e.preventDefault();
        const page = parseInt(jumpToPage, 10);
        if (!isNaN(page) && page > 0 && page <= pageCount) {
            setPagination(p => ({ ...p, pageIndex: page - 1 }));
        }
        setJumpToPage('');
    };

    const handlePageSizeChange = (e) => {
        setPagination(p => ({
            ...p,
            pageSize: Number(e.target.value),
            pageIndex: 0
        }));
    };

    // useCallback keeps the reference stable so memoised <TableRow> children
    // don't re-render every time Reactable re-renders for unrelated reasons.
    const toggleRow = useCallback((index) => {
        setExpandedRows(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    }, []);

    const getAlignmentClass = alignmentClassFor;

    // Padding only depends on rowSize — memoise so it's not recomputed per cell.
    const rowPaddingClass = useMemo(() => {
        switch (rowSize) {
            case 'sm': return 'py-2';
            case 'lg': return 'py-6';
            case 'md':
            default: return 'py-4';
        }
    }, [rowSize]);

    // Computed once per (columns, renderSubComponent) instead of per cell.
    // Mirrors the original `isFirstNonFixedColumn` logic but as a single int.
    const firstNonFixedColIndex = useMemo(() => {
        if (!columns.some(c => !c.fixed)) return -1;
        return renderSubComponent ? 1 : 0;
    }, [columns, renderSubComponent]);

    // Stable callbacks so the memoised <TableHeader> can actually skip
    // re-renders when only the body needs updating.
    const handleClearAllFilters = useCallback(() => setColumnFilters({}), []);

    // Reset to first page when filters / sort / search change. Applies to
    // both client- and server-side modes — server-side previously kept the
    // old pageIndex and could fetch a page that didn't exist for the new
    // filter set. The `p.pageIndex === 0 ? p : ...` guard prevents a no-op
    // state update on mount and on already-page-0 transitions.
    // `skipFirstPageResetRef` lets a restored pagination survive its mount
    // pass — without it, the first effect run after Back-restore would reset
    // pageIndex to 0 and discard the restored page.
    const skipFirstPageResetRef = useRef(restored != null);
    useEffect(() => {
        if (skipFirstPageResetRef.current) {
            skipFirstPageResetRef.current = false;
            return;
        }
        setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
    }, [globalFilter, columnFilters, sorting]);

    // --- State persistence for back-navigation ---
    // Build a fresh snapshot each render via a ref so the unmount cleanup
    // sees the latest values (closures captured in cleanups are otherwise
    // pinned to the first render).
    const buildSnapshotRef = useRef(null);
    buildSnapshotRef.current = () => ({
        globalFilter,
        columnFilters,
        sorting,
        pagination,
        isDraft,
        date,
        month: serializeDayjs(month),
        year: serializeDayjs(year),
        fromDate: serializeDayjs(fromDate),
        toDate: serializeDayjs(toDate),
        expandedRows,
        scrollY: scrollSnapshotRef.current.window,
        tableScrollTop: scrollSnapshotRef.current.table,
    });

    // Save on every state change.
    useEffect(() => {
        if (disableStateCache) return;
        try {
            sessionStorage.setItem(cacheKey, JSON.stringify(buildSnapshotRef.current()));
        } catch {
            /* quota / privacy mode — ignore */
        }
    }, [
        cacheKey, disableStateCache,
        globalFilter, columnFilters, sorting, pagination, isDraft,
        date, month, year, fromDate, toDate, expandedRows,
    ]);

    // Save one more time on unmount so the absolute-latest scroll position
    // is captured (scroll alone doesn't trigger the change-based save above).
    useEffect(() => {
        return () => {
            if (disableStateCache) return;
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify(buildSnapshotRef.current()));
            } catch {
                /* ignore */
            }
        };
    }, [cacheKey, disableStateCache]);

    // Track scroll positions in refs (no re-renders).
    useEffect(() => {
        if (disableStateCache) return;
        const tableEl = tableScrollContainerRef.current;
        const onWindowScroll = () => { scrollSnapshotRef.current.window = window.scrollY; };
        const onTableScroll = () => {
            if (tableEl) scrollSnapshotRef.current.table = tableEl.scrollTop;
        };
        window.addEventListener('scroll', onWindowScroll, { passive: true });
        tableEl?.addEventListener('scroll', onTableScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onWindowScroll);
            tableEl?.removeEventListener('scroll', onTableScroll);
        };
    }, [disableStateCache]);

    // Restore scroll once data populates after a back-navigation.
    useLayoutEffect(() => {
        if (scrollRestoredRef.current) return;
        if (!restored) return;
        if (!data || data.length === 0) return;
        scrollRestoredRef.current = true;
        if (restored.scrollY) {
            window.scrollTo(0, restored.scrollY);
        }
        if (tableScrollContainerRef.current && restored.tableScrollTop) {
            tableScrollContainerRef.current.scrollTop = restored.tableScrollTop;
        }
    }, [data, restored]);

    return (
        <div
            className={cn(
                "shadow-lg rounded-lg overflow-hidden border border-gray-200 bg-white flex flex-col min-h-0 dark:bg-gray-900",
                fillHeight && "flex-1",
                className,
            )}
        >
            {/* Header */}
            <TableHeader
                pagename={pagename}
                tableTitle={tableTitle}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                filterableColumns={filterableColumns}
                columnFilters={columnFilters}
                onApplyFilters={setColumnFilters}
                onClearFilters={handleClearAllFilters}
                sorting={sorting}
                pagination={pagination}
                isDraft={isDraft}
                setIsDraft={setIsDraft}
                url={url}
                isDateRequired={isDateRequired}
                isCSVRequired={isCSVRequired}
                isPDFRequired={isPDFRequired}
                isDraftRequired={isDraftRequired}
                isMonthYearRequired={isMonthYearRequired}
                isOptionalDateWithMonthYear={isOptionalDateWithMonthYear}
                isFromToDateRequired={isFromToDateRequired}
                date={date}
                setDate={setDate}
                month={month}
                setMonth={setMonth}
                year={year}
                setYear={setYear}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                extras={extras}
                printFilter={printFilter}
            >
                {children}
            </TableHeader>

            {/* Loader */}
            {
                loading ? (
                    <div
                        className={cn(
                            "flex justify-center items-center bg-white dark:bg-gray-900",
                            fillHeight ? "flex-1 min-h-0" : "py-8",
                        )}
                        style={!fillHeight && tableHeight ? { minHeight: tableHeight } : undefined}
                    >
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                    </div>
                ) : (
                    <div
                        ref={tableScrollContainerRef}
                        className={cn(
                            "overflow-x-auto bg-white dark:bg-gray-900",
                            fillHeight && "flex-1 min-h-0 overflow-y-auto",
                        )}
                    >
                        {/* Table */}
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    {renderSubComponent && (
                                        <th className="sticky top-0 z-20 w-12 bg-gray-50 dark:bg-gray-800"></th>
                                    )}
                                    {columns.map(({ accessor, Header, sortable, fixed, align, width, maxWidth }) => (
                                        <th
                                            key={accessor}
                                            scope="col"
                                            className={cn(
                                                "px-3 sm:px-6 py-3 capitalize tracking-wider font-medium text-gray-500 dark:text-gray-300 text-xs sticky top-0 z-0 bg-gray-100 dark:bg-gray-800 whitespace-nowrap", // ⬅ prevents wrapping
                                                getAlignmentClass(align),
                                                fixed && "z-[9] sticky",
                                                fixed && (align === "right" ? "right-0" : "left-0"),
                                                fixed && renderSubComponent && align !== "right" && "left-12",
                                            )}
                                            style={{
                                                width: width || undefined,
                                                maxWidth: maxWidth || undefined,
                                                minWidth: width || undefined
                                            }}
                                        >
                                            {sortable ? (
                                                <button
                                                    onClick={() => handleSort(accessor)}
                                                    className={cn(
                                                        "flex items-center gap-1 focus:outline-none",
                                                        align === "center"
                                                            ? "mx-auto"
                                                            : align === "right"
                                                                ? "ml-auto"
                                                                : "mr-auto"
                                                    )}
                                                >
                                                    <span>{Header}</span>
                                                    <SortIcon
                                                        direction={
                                                            sorting.accessor === accessor
                                                                ? sorting.direction
                                                                : "none"
                                                        }
                                                    />
                                                </button>
                                            ) : (
                                                Header
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedData?.length > 0 ? (
                                    paginatedData.map((row, i) => {
                                        // Stable per-paginated-page key. Prefer a real row id when
                                        // present; otherwise scope the index to the current page so
                                        // paginate/sort/filter don't reuse the same React key for
                                        // different data.
                                        const rowKey = row?.id ?? `${pagination.pageIndex}-${i}`;
                                        return (
                                            <TableRow
                                                key={rowKey}
                                                row={row}
                                                rowIndex={i}
                                                rowKey={rowKey}
                                                columns={columns}
                                                renderSubComponent={renderSubComponent}
                                                isExpanded={!!expandedRows[i]}
                                                toggleRow={toggleRow}
                                                onRowClick={onRowClick}
                                                onRowDoubleClick={onRowDoubleClick}
                                                highlightClass={getRowHighlightClass(row)}
                                                rowPaddingClass={rowPaddingClass}
                                                firstNonFixedColIndex={firstNonFixedColIndex}
                                                expandedContentColSpan={columns.length + (renderSubComponent ? 1 : 0)}
                                            />
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={columns.length + (renderSubComponent ? 1 : 0)}
                                            className="text-center py-12 sm:py-16"
                                        >
                                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                                <EmptyIcon />
                                                <h3 className="mt-4 text-sm sm:text-lg font-semibold">
                                                    No Records Found
                                                </h3>
                                                <p className="text-xs sm:text-sm">
                                                    There is no data matching your query.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {/* Pagination */}
            {
                pageCount > 0 && !loading && (
                    <div className="px-3 sm:px-6 py-3 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shrink-0">
                        {/* Mobile: Prev / Next only */}
                        <div className="flex sm:hidden justify-between">
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                }
                                disabled={pagination.pageIndex === 0}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                }
                                disabled={pagination.pageIndex >= pageCount - 1}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>

                        {/* Tablet + Desktop */}
                        <div className="hidden sm:flex flex-1 items-center justify-between">
                            {/* Rows per page */}
                            <div className="flex items-center gap-x-2">
                                <span className="text-xs text-gray-700 dark:text-gray-300">
                                    Rows per page
                                </span>
                                <SelectField
                                    size="compact"
                                    value={pagination.pageSize}
                                    onChange={handlePageSizeChange}
                                    className="w-auto min-w-[4rem]"
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </SelectField>
                            </div>

                            {/* Page info + navigation */}
                            <div className="flex items-center gap-x-4 lg:gap-x-6">
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                    Page{" "}
                                    <span className="font-medium">{pagination.pageIndex + 1}</span> of{" "}
                                    <span className="font-medium">{pageCount}</span>
                                </p>

                                {/* Jump to page (desktop only) */}
                                <form
                                    onSubmit={handleJumpToPage}
                                    className="hidden lg:flex items-center gap-x-2"
                                >
                                    <label
                                        htmlFor="jump-to-page"
                                        className="text-xs text-gray-700 dark:text-gray-300"
                                    >
                                        Go to page:
                                    </label>
                                    <input
                                        id="jump-to-page"
                                        type="number"
                                        value={jumpToPage}
                                        onChange={(e) => setJumpToPage(e.target.value)}
                                        className="w-16 px-2 py-1 border border-input rounded-md text-xs shadow-sm bg-background text-foreground focus:border-sidebar-primary focus:ring focus:ring-sidebar-primary/20"
                                        min="1"
                                        max={pageCount}
                                    />
                                    <button
                                        type="submit"
                                        className="px-3 py-1 border rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-sidebar-primary dark:hover:bg-indigo-700 hover:bg-gray-100 focus:outline-none"
                                    >
                                        Go
                                    </button>
                                </form>

                                {/* Navigation buttons */}
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPagination((p) => ({ ...p, pageIndex: 0 }))}
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                        }
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: pageCount - 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsRightIcon />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};